import React, { useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import DateControl from "../../../components/Fields/DateControl";
import EmailField from "../../../components/Fields/EmailField";
import Field from "../../../components/Fields/Field";
import FieldLabel from "../../../components/Fields/FieldLabel";
import SelectListField from "../../../components/Fields/SelectListField";
import TextboxControl from "../../../components/Fields/TextboxControl";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { useNotifications } from "../../../contexts/notificationContext";
import DataService from "../../../services/data.service";
import { Result } from "../../../types/Result";
import FormatUtils from "../../../utils/FormatUtils";

interface IProps {
    show: boolean
    onHide: (employeeId?: number) => void
}

interface EmployeeCreateModel {
    loginName: string
    firstName: string
    preposition?: string
    genderId: number
    lastName: string
    email: string
    language: string
    userId?: number
    departmentId?: number
    referenceCode?: string
    beginDate: string
    endDate?: string
    copyRolesFrom_UserId?: number
}

const CreateEmployeePopup: React.FC<IProps> = ({ show, onHide }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<EmployeeCreateModel>({
        loginName: '',
        firstName: '',
        lastName: '',
        email: '',
        language: '',
        genderId: 0,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: ''
    });

    const departments = useSelectListOptions('api/v1/selectlist/Departments', '-- Select department');
    const genders = useSelectListOptions('api/v1/selectlist/Genders', '-- Select gender');
    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- Select language');
    const users = useSelectListOptions('api/v1/selectlist/Users/?activeOnly=true', '-- Select user');

    function submit() {
        if (formData) {
            DataService
                .create<number>(`api/v1/Employees`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        if (onHide)
                            onHide(response.data);
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'firstName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
            { name: 'prePosition', label: 'Name', labelSize: 3, validationRules: [{ type: 'isMaxLength', maxLength: 250 }] },
            { name: 'lastName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
            { name: 'genderId', label: 'Gender', labelSize: 3 },
            { name: 'departmentId', label: 'Department', labelSize: 3 },
            { name: 'language', label: 'Language', labelSize: 3 },
            { name: 'loginName', label: 'Login name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
            { name: 'email', label: 'Email', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
            { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
            { name: 'copyRolesFrom_UserId', label: 'Copy roles from user', labelSize: 3 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getLabelProps, getControlProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Create employee'} >
            <Popup.Body>
                <Field {...getLabelProps('firstName')}>
                    <TextboxControl {...getControlProps('firstName')} />
                    <TextboxControl {...getControlProps('prePosition')} width={'40%'} />
                    <TextboxControl {...getControlProps('lastName')} />
                </Field>
                <SelectListField {...getFieldProps('genderId')} options={genders} />
                <SelectListField {...getFieldProps('departmentId')} options={departments} />
                <SelectListField {...getFieldProps('language')} options={languages} />
                <TextboxField {...getFieldProps('loginName')} />
                <EmailField {...getFieldProps('email')} />
                <Field {...getLabelProps('beginDate')}>
                    <DateControl {...getControlProps('beginDate')} />
                    <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                    <DateControl {...getControlProps('endDate')} />
                </Field>
                <SelectListField {...getFieldProps('copyRolesFrom_UserId')} options={users} />
            </Popup.Body>
            <Popup.Footer>
                <SaveButton text='Create' {...getSubmitButtonProps()} />
            </Popup.Footer>
        </Popup>
    );
}
export default CreateEmployeePopup;